import { OperationVariables } from '@apollo/client';
import { Sizes } from '@sede-enterprise/shell-ds-react-framework';
import { useQueryErrorAlert } from 'common-react/utils/apollo/hooks/useQueryErrorAlert';
import { useRefetchOnError } from 'common-react/utils/apollo/hooks/useRefetchOnError';
import { Alert } from 'components/Alert/Alert';
import { Page } from 'components/Layout/Page/Page';
import {
  CustomDivider,
  MobileSectionHeader,
  SectionHeader,
  SectionLeft,
  SectionRight,
} from 'components/Layout/SectionHeader/SectionHeader';
import { NavBarMobile } from 'components/NavBar/NavBarMobile/NavBarMobile';
import { GET_COMPANY } from 'components/NavBar/queries';
import { SiteSelector } from 'components/SiteSelector';
import Carousel from 'components/storybook-migration/Carousel/Carousel';
import CarouselItem from 'components/storybook-migration/Carousel/CarouselItem';
import { DYNATRACE_ACTIONS } from 'constants/dynatrace-actions';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useDimensions } from 'hooks/useDimensions/useDimensions';
import { usePlutoApiQuery } from 'hooks/useQuery/plutoApi';
import { usePlutoApiTradeDataSubscriber } from 'hooks/useTradeDataSubscriber/useTradeDataSubscriber';
import { ContractedVolume } from 'pages/Optimisation/components/ContractedVolume/ContractedVolume';
import { DateSelector } from 'pages/Optimisation/components/DateSelector';
import { CalendarDot } from 'pages/Optimisation/components/DateSelector/CalendarDot';
import { MarketTabs } from 'pages/Optimisation/components/MarketTabs/MarketTabs';
import { CompanySiteSelectorToggle } from 'pages/Optimisation/components/mobile/CompanySiteSelectorToggle';
import { RevenueBreakdownCard } from 'pages/Optimisation/components/SummaryCards/RevenueBreakdownCard/RevenueBreakdownCard';
import { RevenuePerMWCard } from 'pages/Optimisation/components/SummaryCards/RevenuePerMW/RevenuePerMW';
import { TotalRevenueCard } from 'pages/Optimisation/components/SummaryCards/TotalRevenue/TotalRevenue';
import {
  CHART_CONSTANTS,
  MARKET_FILTER_VALUES,
} from 'pages/Optimisation/constants';
import {
  SummaryCardContainer,
  SummaryCardWrapper,
} from 'pages/Optimisation/Optimisation.styled';
import {
  GET_SITE_TRADE_COUNT,
  GET_SITE_TRADE_SUMMARY,
} from 'pages/Optimisation/queries';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useStore } from 'store';
import { Site } from 'types/site';
import {
  dynatraceTrackInstantAction,
  useDynatraceActionTrackingContext,
} from 'utils/dynatrace';
import { endOfTradingDay, startOfTradingDay } from 'utils/optimisation';

dayjs.extend(utc);

export const Optimisation = ({ sites }: { sites: Array<Site> }) => {
  const { company, site } = useParams();
  const { isMobileOrTablet } = useDimensions();
  const [marketFilter, setMarketFilter] = useState(
    CHART_CONSTANTS.marketFilters[0].value,
  );
  useDynatraceActionTrackingContext();

  const handleChipFiltering = (option: MARKET_FILTER_VALUES) => {
    switch (option) {
      case MARKET_FILTER_VALUES.All:
        dynatraceTrackInstantAction(DYNATRACE_ACTIONS.VIEW_ALL_ALL_MARKETS);
        break;
      case MARKET_FILTER_VALUES.Wholesale:
        dynatraceTrackInstantAction(DYNATRACE_ACTIONS.VIEW_WHOLESALE_MARKETS);
        setMarketFilter(option);
        break;
      case MARKET_FILTER_VALUES.Ancillary:
        dynatraceTrackInstantAction(DYNATRACE_ACTIONS.VIEW_ANCILLARY_MARKETS);
        setMarketFilter(option);
        break;
      case MARKET_FILTER_VALUES.BalancingMechanism:
        dynatraceTrackInstantAction(DYNATRACE_ACTIONS.VIEW_BALANCING_MECHANISM);
        setMarketFilter(option);
        break;
    }
    setMarketFilter(option);
  };

  const [currentDate, selectedSite, setSelectedSite] = useStore((state) => [
    state.currentDate,
    state.selectedSite,
    state.setSelectedSite,
  ]);

  const [tradeCountMap, setTradeCountMap] = useState<{
    allMarkets: { [key: string]: number };
    ancillary: { [key: string]: number };
    wholesale: { [key: string]: number };
    balancingMechanism: { [key: string]: number };
  }>({
    allMarkets: {},
    ancillary: {},
    wholesale: {},
    balancingMechanism: {},
  });

  const siteId = useMemo(() => (site ? +site : undefined), [site]);

  usePlutoApiTradeDataSubscriber(siteId);

  const { setAlert, alert } = useStore((state) => state);

  const queryVariables = {
    siteId: site,
    start: startOfTradingDay(currentDate).toISOString(),
    end: endOfTradingDay(currentDate).toISOString(),
  };

  const { data: companyDetail } = usePlutoApiQuery<{
    company: { name: string };
  }>(GET_COMPANY, {
    skip: !company || !isMobileOrTablet,
    variables: { id: company },
  });

  const { data, loading, error, refetch } = usePlutoApiQuery<
    any,
    OperationVariables
  >(GET_SITE_TRADE_SUMMARY, {
    skip: !site,
    variables: queryVariables,
  });

  useRefetchOnError(3, refetch, error);

  const setErrorAlert = useCallback(() => {
    setAlert(
      'warning',
      `We couldn't fetch your data, possibly due to a temporary server issue. Please try refreshing the page`,
      'Refresh',
      () => window.location.reload(),
    );
  }, [setAlert]);

  useQueryErrorAlert(error, setErrorAlert);

  const tradeCountResult = usePlutoApiQuery(GET_SITE_TRADE_COUNT, {
    // Query for trade count for this year and the previous year once
    skip: !site,
    variables: {
      siteId: site,
      start: dayjs(currentDate).subtract(1, 'year').format('YYYY-MM-DD'),
      end: dayjs(currentDate).add(1, 'year').format('YYYY-MM-DD'),
    },
  });

  useEffect(() => {
    const findSelectedSite = sites?.find((x) => x.id === site)?.name;
    setSelectedSite(findSelectedSite);
  }, [site, sites]);

  useEffect(() => {
    if (!tradeCountResult.data) {
      return;
    }
    const tradeCounts = tradeCountResult.data.site.tradeCount;

    const flattenTradeCountArrayToMap = (
      tradeCounts: { date: string; count: number }[],
    ) => {
      return tradeCounts.reduce(
        (
          acc: { [key: string]: number },
          tradeCount: { date: string; count: number },
        ) => {
          const dateStr = dayjs.utc(tradeCount.date).format('YYYY-MM-DD');
          acc[dateStr] = tradeCount.count;
          return acc;
        },
        {},
      );
    };

    const tradeCountsMap = {
      allMarkets: flattenTradeCountArrayToMap(tradeCounts.allMarkets),
      ancillary: flattenTradeCountArrayToMap(tradeCounts.ancillary),
      wholesale: flattenTradeCountArrayToMap(tradeCounts.wholesale),
      balancingMechanism: flattenTradeCountArrayToMap(
        tradeCounts.balancingMechanism,
      ),
    };

    setTradeCountMap(tradeCountsMap);
  }, [tradeCountResult.data]);

  const cellRender = (current: number | Dayjs, info: any) => {
    const currentDayISO = dayjs.utc(current).local().format('YYYY-MM-DD');
    const isCurrentDay = dayjs(current)
      .local()
      .isSame(dayjs(currentDate), 'day');
    const isActive =
      !isCurrentDay && (tradeCountMap[marketFilter][currentDayISO] ?? 0) > 0;

    return <CalendarDot {...{ current, info, isActive }} />;
  };

  return (
    <div>
      {isMobileOrTablet ? (
        <NavBarMobile>
          <CompanySiteSelectorToggle
            companyName={companyDetail?.company?.name}
            site={selectedSite}
          />
        </NavBarMobile>
      ) : null}
      {alert.visible ? <Alert data-testid={'alert-banner'} /> : null}

      {isMobileOrTablet ? (
        <MobileSectionHeader id={'mobile-date-header'}>
          <DateSelector
            isMobile={isMobileOrTablet}
            disabled={false}
            loading={false}
            cellRender={cellRender}
          />
        </MobileSectionHeader>
      ) : (
        <SectionHeader>
          <SectionLeft>
            <h1>Optimisation</h1>
            <SiteSelector sites={sites} />
          </SectionLeft>
          <SectionRight>
            <DateSelector
              isMobile={isMobileOrTablet}
              disabled={false}
              loading={false}
              cellRender={cellRender}
            />
          </SectionRight>
        </SectionHeader>
      )}

      <div>
        <CustomDivider size={Sizes.Small} prominence={'subtle'} />
        <SectionHeader style={{ paddingTop: 0, paddingBottom: 0 }} hasShadow>
          <MarketTabs<MARKET_FILTER_VALUES>
            options={CHART_CONSTANTS.marketFilters}
            onChange={handleChipFiltering}
            selected={marketFilter}
          />
        </SectionHeader>
      </div>
      <Page>
        {isMobileOrTablet ? (
          <SummaryCardContainer>
            <SummaryCardWrapper className="summaryCardWrapper">
              <Carousel contentStyle={{ width: '100%' }}>
                <CarouselItem>
                  <TotalRevenueCard
                    data={data}
                    loading={!data && loading}
                    marketFilter={marketFilter}
                  />
                </CarouselItem>
                <CarouselItem>
                  <RevenuePerMWCard
                    data={data}
                    loading={!data && loading}
                    marketFilter={marketFilter}
                  />
                </CarouselItem>
                <CarouselItem>
                  <RevenueBreakdownCard
                    data={data}
                    loading={!data && loading}
                    marketFilter={marketFilter}
                  />
                </CarouselItem>
              </Carousel>
            </SummaryCardWrapper>
          </SummaryCardContainer>
        ) : (
          <SummaryCardWrapper className="summaryCardWrapper">
            <TotalRevenueCard
              data={data}
              loading={!data && loading}
              marketFilter={marketFilter}
            />

            <RevenuePerMWCard
              data={data}
              loading={!data && loading}
              marketFilter={marketFilter}
            />

            <RevenueBreakdownCard
              data={data}
              loading={!data && loading}
              marketFilter={marketFilter}
            />
          </SummaryCardWrapper>
        )}

        <ContractedVolume marketFilter={marketFilter} />
      </Page>
    </div>
  );
};
